import { useCallback, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";

interface QuestionProps {
  question: string;
  description?: string;
  color: string;
  alternatives: Array<{ reference: string; text: string; correct: boolean }>;
}

const questions: QuestionProps[] = [
  {
    question:
      "Questão 01: O canto triste dos conquistados: os últimos dias de Tenochtitlán. Nos caminhos jazem dardos quebrados; os cabelos estão espalhados. Destelhadas estão as casas, Vermelhas estão as águas, os rios, como se alguém as tivesse tingido, Nos escudos esteve nosso resguardo, mas os escudos não detêm a desolação…",
    description:
      "O texto é um registro asteca, cujo sentido está relacionado ao(à)",
    color: "text-blue-500",
    alternatives: [
      {
        reference: "A",
        text: "tragédia causada pela destruição da cultura desse povo.",
        correct: false,
      },
      {
        reference: "B",
        text: "tentativa frustada de resistência a um poder considerado superior.",
        correct: true,
      },
      {
        reference: "C",
        text: "extermínio das populações indígenas pelo Exército espanhol.",
        correct: false,
      },
      {
        reference: "D",
        text: "dissolução da memória sobre os feitos de seus antepassados.",
        correct: false,
      },
      {
        reference: "E",
        text: "profetização das consequências da colonização da América.",
        correct: false,
      },
    ],
  },
  {
    question:
      "Questão 02: No século XX, o transporte rodoviário e a aviação civil aceleraram o intercâmbio de pessoas e mercadorias, fazendo com que as distâncias e a percepção subjetiva das mesmas se reduzissem constantemente. É possível apontar uma tendência de universalização em vários campos, por exemplo, na globalização da economia, no armamentismo nuclear, na manipulação genética, entre outros. HABERMAS, J. A constelação pós-nacional:ensaios políticos. São Paulo: Littera Mundi, 2001 (adaptado).",
    description:
      "Os impactos e efeitos dessa universalização, conforme descritos no texto, podem ser analisados do ponto de vista moral, o que leva à defesa da criação de normas universais que estejam de acordo com:",
    color: "text-blue-500",
    alternatives: [
      {
        reference: "A",
        text: "os valores culturais praticados pelos diferentes povos em suas tradições e costumes locais.",
        correct: false,
      },
      {
        reference: "B",
        text: "os pactos assinados pelos grandes líderes políticos, os quais dispõem de condições para tomar decisões.",
        correct: false,
      },
      {
        reference: "C",
        text: "os sentimentos de respeito e fé no cumprimento de valores religiosos relativos à justiça divina.",
        correct: false,
      },
      {
        reference: "D",
        text: "os sistemas políticos e seus processos consensuais e democráticos de formação de normas gerais.",
        correct: true,
      },
      {
        reference: "E",
        text: "os imperativos técnico-científicos, que determinam com exatidão o grau de justiça das normas.",
        correct: false,
      },
    ],
  },
  {
    question:
      "Questão 03: A prosperidade induzida pela emergência das máquinas de tear escondia uma acentuada perda de prestígio. Foi nessa idade de ouro que os artesãos, ou os tecelões temporários, passaram a ser denominados, de modo genérico, tecelões de teares manuais. Exceto em alguns ramos especializados, os velhos artesãos foram colocados lado a lado com novos imigrantes, enquanto pequenos fazendeiros-tecelões abandonaram suas pequenas propriedades para se concentrar na atividade de tecer. Reduzidos à completa dependência dos teares mecanizados ou dos fornecedores de matéria-prima, os tecelões ficaram expostos a sucessivas reduções dos rendimentos. THOMPSON, E. P. The making of the english working class. Harmondsworth: Penguin Books, 1979 (adaptado).",
    description:
      "Com a mudança tecnológica ocorrida durante a Revolução Industrial, a forma de trabalhar alterou-se porque",
    color: "text-blue-500",
    alternatives: [
      {
        reference: "A",
        text: "a invenção do tear propiciou o surgimento de novas relações sociais.",
        correct: false,
      },
      {
        reference: "B",
        text: "os tecelões mais hábeis prevaleceram sobre os inexperientes.",
        correct: false,
      },
      {
        reference: "C",
        text: "os novos teares exigiam treinamento especializado para serem operados.",
        correct: false,
      },
      {
        reference: "D",
        text: "os artesãos, no período anterior, combinavam a tecelagem com o cultivo de subsistência.",
        correct: true,
      },
      {
        reference: "E",
        text: "os trabalhadores não especializados se apropriaram dos lugares dos antigos artesãos nas fábricas.",
        correct: false,
      },
    ],
  },
  {
    question:
      "Questão 04: No final do século XVI, na Bahia, Guiomar de Oliveira denunciou Antônia Nóbrega à Inquisição. Segundo o depoimento, esta lhe dava “uns pós não sabe de quê, e outros pós de osso de finado, os quais pós ela confessante deu a beber em vinho ao dito seu marido para ser seu amigo e serem bem-casados, e que todas estas coisas fez tendo-lhe dito a dita Antônia e ensinado que eram coisas diabólicas e que os diabos lha ensinaram”. ARAÚJO, E. O teatro dos vícios. Transgressão e transigência na sociedade urbana colonial. Brasília: UnB/José Olympio, 1997.",
    description: "Do ponto de vista da Inquisição,",
    color: "text-blue-500",
    alternatives: [
      {
        reference: "A",
        text: "o problema dos métodos citados no trecho residia na dissimulação, que acabava por enganar o enfeitiçado.",
        correct: false,
      },
      {
        reference: "B",
        text: "o diabo era um concorrente poderoso da autoridade da Igreja e somente a justiça do fogo poderia eliminá-lo.",
        correct: false,
      },
      {
        reference: "C",
        text: "os ingredientes em decomposição das poções mágicas eram condenados porque afetavam a saúde da população.",
        correct: false,
      },
      {
        reference: "D",
        text: "as feiticeiras representavam séria ameaça à sociedade, pois eram perceptíveis suas tendências feministas.",
        correct: false,
      },
      {
        reference: "E",
        text: "os cristãos deviam preservar a instituição do casamento recorrendo exclusivamente aos ensinamentos da Igreja.",
        correct: true,
      },
    ],
  },
  {
    question:
      "Questão 05: Durante a realeza, e nos primeiros anos republicanos, as leis eram transmitidas oralmente de uma geração para outra. A ausência de uma legislação escrita permitia aos patrícios manipular a justiça conforme seus interesses. Em 451 a.C., porém, os plebeus conseguiram eleger uma comissão de dez pessoas – os decênviros – para escrever as leis. Dois deles viajaram a Atenas, na Grécia, para estudar a legislação de Sólon. COULANGES, F. A cidade antiga. São Paulo. Martins Fontes, 2000.",
    description:
      "A superação da tradição jurídica oral no mundo antigo, descrita no texto, esteve relacionada à",
    color: "text-blue-500",
    alternatives: [
      {
        reference: "A",
        text: "adoção do sufrágio universal masculino.",
        correct: false,
      },
      {
        reference: "B",
        text: "extensão da cidadania aos homens livres.",
        correct: true,
      },
      {
        reference: "C",
        text: "afirmação de instituições democráticas.",
        correct: false,
      },
      {
        reference: "D",
        text: "implantação de direitos sociais.",
        correct: false,
      },
      {
        reference: "E",
        text: "tripartição dos poderes políticos.",
        correct: false,
      },
    ],
  },
  {
    question:
      "Questão 06: A moralidade, Bentham exortava, não é uma questão de agradar a Deus, muito menos de fidelidade a regras abstratas. A moralidade é a tentativa de criar a maior quantidade de felicidade possível neste mundo. Ao decidir o que fazer, deveríamos, portanto, perguntar qual curso de conduta promoveria a maior quantidade de felicidade para todos aqueles que serão afetados. (RACHELS, J. Os elementos dafilosofia moral. Barueri-SP: Manole, 2006.)",
    description:
      "Os parâmetros da ação indicados no texto estão em conformidade com uma",
    color: "text-blue-500",
    alternatives: [
      {
        reference: "A",
        text: "fundamentação científica de viés positivista.",
        correct: false,
      },
      {
        reference: "B",
        text: "convenção social de orientação normativa.",
        correct: false,
      },
      {
        reference: "C",
        text: "transgressão comportamental religiosa.",
        correct: false,
      },
      {
        reference: "D",
        text: "racionalidade de caráter pragmático.",
        correct: true,
      },
      {
        reference: "E",
        text: "inclinação de natureza passional.",
        correct: false,
      },
    ],
  },
  {
    question:
      "Questão 07:  Não é verdade que estão ainda cheios de velhice espiritual aqueles que nos dizem: “Que fazia Deus antes de criar o céu e a terra? Se estava ocioso e nada realizava”, dizem eles, “por que não ficou sempre assim no decurso dos séculos, abstendo-se, como antes, de toda ação? Se existiu em Deus um novo movimento, uma vontade nova para dar o ser a criaturas que nunca antes criara, como pode haver verdadeira eternidade, se n’Ele aparece uma vontade que antes não existia?” (AGOSTINHO. Confissões. São Paulo: Abril Cultural, 1984.)",
    description:
      "A questão da eternidade, tal como abordada pelo autor, é um exemplo da reflexão filosófica sobre a(s)",
    color: "text-blue-500",
    alternatives: [
      {
        reference: "A",
        text: "essência da ética cristã.",
        correct: false,
      },
      {
        reference: "B",
        text: "natureza universal da tradição.",
        correct: false,
      },
      {
        reference: "C",
        text: "certezas inabaláveis da experiência.",
        correct: false,
      },
      {
        reference: "D",
        text: "abrangência da compreensão humana.",
        correct: true,
      },
      {
        reference: "E",
        text: "interpretações da realidade circundante.",
        correct: false,
      },
    ],
  },
  {
    question:
      "Questão 08:  Será que as coisas pareceriam diferentes se, de fato, todas elas existissem apenas na sua mente — se tudo o que você julgasse ser o mundo externo real fosse apenas um sonho ou alucinação gigante, de que você jamais fosse despertar? Se assim fosse, então é claro que você nunca poderia despertar, como faz quando sonha, pois significaria que não há mundo “real” no qual despertar. Logo, não seria exatamente igual a um sonho ou alucinação normal. (NAGEL, T. Uma breve introdução à filosofia. São Paulo: Martins Fontes, 2011.)",
    description:
      "O texto confere visibilidade a uma doutrina filosófica contemporânea conhecida como:",
    color: "text-blue-500",
    alternatives: [
      {
        reference: "A",
        text: "Personalismo, que vincula a realidade circundante aos domínios do pessoal.",
        correct: false,
      },
      {
        reference: "B",
        text: "Falsificacionismo, que estabelece ciclos de problemas para refutar a conjectura.",
        correct: false,
      },
      {
        reference: "C",
        text: "Falibilismo, que rejeita mecanismos mentais para sustentar uma crença inequívoca.",
        correct: false,
      },
      {
        reference: "D",
        text: "Idealismo, que nega a existência de objetivos independentemente do trabalho cognoscente.",
        correct: false,
      },
      {
        reference: "E",
        text: "Solipsismo, que reconhece limitações cognitivas para compreender uma experiência compartilhada.",
        correct: true,
      },
    ],
  },
  {
    question:
      "Questão 09:  Na maior parte da América Latina, os museus surgiram no século passado, fundados com a intenção de “civilizar”, ou seja, de trazer para o Novo Mundo os padrões científicos e culturais das nações colonizadoras. Os museus seriam, dessa forma, instituições transplantadas, criadas dentro dos ideais positivistas de progresso. Não por acaso, ficaram, em sua maior parte, sujeitos aos moldes clássicos, a partir da valorização de aspectos da cultura erudita, fortemente associados à elite. Era necessário, pois, assumir uma função social de maior alcance e ocupar um espaço relevante, capaz de atrair grande quantidade de público. (BARRETO, M. Turismo e legado cultural. Campinas: Papirus, 2002 (adaptado).)",
    description:
      "A transformação de um número cada vez mais expressivo de museus latino-americanos em espaços destinados a atividades lúdicas e reflexivas está associada ao rompimento com o(a)",
    color: "text-blue-500",
    alternatives: [
      {
        reference: "A",
        text: "ideal de educação tradicional.",
        correct: true,
      },
      {
        reference: "B",
        text: "utilização de novas tecnologias.",
        correct: false,
      },
      {
        reference: "C",
        text: "modelo de atrações segmentadas.",
        correct: false,
      },
      {
        reference: "D",
        text: "participação do setor empresarial.",
        correct: false,
      },
      {
        reference: "E",
        text: "resgate de sentimentos nacionalistas.",
        correct: false,
      },
    ],
  },
  {
    question:
      "Questão 10:  Desde o século XII que a cristandade ocidental era agitada pelo desafio lançado pela cultura profana – a dos romances de cavalaria, mas também a cultura folclórica dos camponeses e igualmente a dos citadinos, de caráter mais jurídicos – à cultura eclesiástica, cujo veículo era o latim. Francisco de Assis veio alterar a situação, propondo aos seus ouvintes uma mensagem acessível a todos e, simultaneamente, enobrecendo a língua vulgar através do seu uso na religião (VAUCHEZ, A. A espiritualidade da Idade Média Ocidental, séc. VIII-XIII. Lisboa: Estampa, 1995.)",
    description:
      "O comportamento desse religioso demonstra uma preocupação com as características assumidas pela Igreja e com as desigualdades sociais compartilhada no seu tempo pelos(as)",
    color: "text-blue-500",
    alternatives: [
      {
        reference: "A",
        text: "senhores feudais.",
        correct: false,
      },
      {
        reference: "B",
        text: "movimentos heréticos.",
        correct: true,
      },
      {
        reference: "C",
        text: "integrantes das Cruzadas.",
        correct: false,
      },
      {
        reference: "D",
        text: "corporações de ofícios.",
        correct: false,
      },
      {
        reference: "E",
        text: "universidades medievais.",
        correct: false,
      },
    ],
  },
];

interface QuizModalProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

export const QuizModal: React.FC<QuizModalProps> = ({ isOpen, setIsOpen }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAlternative, setSelectedAlternative] = useState<string | null>(
    null
  );
  const [feedback, setFeedback] = useState<string | null>(null);
  const [score, setScore] = useState(0);

  const currentQuestion = useMemo(
    () => questions[currentIndex],
    [currentIndex]
  );

  const handleNext = useCallback(() => {
    setSelectedAlternative(null);
    setFeedback(null);
    setCurrentIndex((prev) => (prev + 1) % questions.length);
  }, []);

  const handlePrev = useCallback(() => {
    setSelectedAlternative(null);
    setFeedback(null);
    setCurrentIndex((prev) => (prev - 1 + questions.length) % questions.length);
  }, []);

  const handleAnswer = useCallback((reference: string, correct: boolean) => {
    setSelectedAlternative(reference);
    if (correct) {
      setScore((prev) => prev + 1);
      setFeedback("Correto! ✅");
    } else {
      setFeedback("Incorreto ❌");
    }
  }, []);

  const finalize = useCallback(() => {
    toast.success(
      `Parabéns! Você acertou ${score} questões. O quiz será reiniciado.`
    );
    setCurrentIndex(0);
    setScore(0);
    setSelectedAlternative(null);
    setFeedback(null);
    setIsOpen(false);
  }, [score]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 px-4">
      <div className="w-full max-w-md sm:max-w-lg md:max-w-2xl bg-white p-6 rounded-2xl shadow-2xl relative z-50 max-h-[90vh] overflow-y-auto">
        <button
          onClick={() => setIsOpen(false)}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
          aria-label="Fechar quiz"
        >
          Fechar
        </button>
        <div className="mb-4 text-center">
          <h2 className="text-2xl font-bold">Quiz de Microdados</h2>
          <p>
            Acertos: {score} / {questions.length}
          </p>
        </div>
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.5 }}
          >
            <p
              className={`text-sm md:text-base ${
                currentQuestion.color || "text-black"
              }`}
            >
              {currentQuestion.question}
            </p>
            {currentQuestion.description && (
              <p className="text-gray-600 mt-2">
                {currentQuestion.description}
              </p>
            )}
            <div className="space-y-2 mt-4">
              {currentQuestion?.alternatives?.map((alt) => (
                <button
                  key={alt.reference}
                  onClick={() => handleAnswer(alt.reference, alt.correct)}
                  disabled={!!selectedAlternative}
                  className={`block w-full p-2 border rounded-md transition-all ${
                    selectedAlternative
                      ? alt.correct
                        ? "bg-green-500 text-white"
                        : selectedAlternative === alt.reference
                        ? "bg-red-500 text-white"
                        : "bg-gray-200"
                      : "bg-white hover:bg-gray-100"
                  }`}
                >
                  {alt.reference}: {alt.text}
                </button>
              ))}
            </div>
            {feedback && (
              <p className="mt-2 text-sm text-center text-red-500">
                {feedback}
              </p>
            )}
          </motion.div>
        </AnimatePresence>
        <div className="flex justify-between mt-4">
          <button
            onClick={handlePrev}
            className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
          >
            Anterior
          </button>
          <button
            onClick={
              currentIndex + 1 === questions.length ? finalize : handleNext
            }
            className={`px-4 py-2 rounded-md transition-all ${
              selectedAlternative
                ? "bg-blue-500 hover:bg-blue-600 text-white"
                : "bg-gray-300 text-gray-700 cursor-not-allowed"
            }`}
            disabled={!selectedAlternative}
          >
            {currentIndex + 1 === questions.length ? "Finalizar" : "Próxima"}
          </button>
        </div>
      </div>
    </div>
  );
};
